// Brij preset — STARTER TEMPLATE ONLY. See docs/WHITE_LABELING.md.
// Tenants do NOT depend on this at runtime; the full brand lives in
// `tenant.tenant_settings.brand_overrides`. This is what the editor's
// "Apply Brij preset" button loads into the form.
// Color/font source: brijlabs-ai/brij-design-system/BRAND.md

import type { BrandConfig } from '../types';

// Stable, unhashed public/ paths — NOT Vite imports (a hashed /assets/ URL saved
// into brand_overrides breaks on rebuild/host change). Source: public/brand/brij/.
const logo = '/brand/brij/logo.svg';
const icon = '/brand/brij/icon.svg';

const brij: BrandConfig = {
  id: 'brij',
  name: 'Brij',
  tagline: 'Intelligence Lab.',
  companyName: 'Brij',
  companyUrl: 'https://brijlabs.ai/',
  pageTitle: 'Brij — Intelligence Lab.',

  logo,
  icon,
  // No `iconDark` — brij's icon SVG is single-color and reads on both themes,
  // so `useBrandAssets` falling back to `icon` in dark mode is correct.
  // No `conciergeIcon` — the Concierge wears the ordinary brand mark.
  // No `anniversaryIcon` / `anniversaryLogo` (nor their dark twins) — the
  // 10-year treatment is M²'s celebration, not brij's; the fallbacks in
  // `useBrandAssets` keep the regular mark and logo on every surface.

  colors: {
    // Primary = Accent Gold (the CTA accent in the brand book).
    primary: '#d4a853',
    primaryHover: '#c79a45',
    // Secondary = Accent Sage. SecondaryLight = Sage Mist.
    secondary: '#b8c478',
    secondaryLight: '#dae0d2',
    secondaryDark: '#9da966',
    // Accent = Sky (third stop of the logo gradient).
    accent: '#98c6e7',
    gradientPrimary: 'linear-gradient(135deg, #E3BC79 0%, #D4DB95 50%, #98C6E7 100%)',
  },

  // Light mode: white-cream surfaces — matches brand-book intent (light, clinical).
  lightMode: {
    bgPrimary: '#ffffff',
    bgSecondary: '#faf9f4',
    bgTertiary: '#f4f3ee',
    bgElevated: '#ffffff',
  },

  // Dark mode: brand book defines no dark variant — tasteful neutral darks
  // with a subtle warm cast to stay tonally aligned with the gold/sage palette.
  darkMode: {
    bgPrimary: '#0e1010',
    bgSecondary: '#161818',
    bgTertiary: '#1d1f1f',
    bgElevated: '#22251f',
  },

  fonts: {
    heading: 'Plus Jakarta Sans',
    body: 'Plus Jakarta Sans',
    accentSerif: 'Arbutus Slab',
    mono: 'JetBrains Mono',
    googleFontsUrl: 'https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=Arbutus+Slab&family=JetBrains+Mono:wght@400;500&display=swap',
  },

  // No `loginEyebrow` — brij has no separate sub-brand/product label distinct
  // from `name`, so the eyebrow falls back to the brand name (M² uses it for
  // "JewishPro"; brij doesn't need it).
  // Same three as default.ts, so they share its key paths.
  loginFeatures: [
    { label: 'login.features.tenantAware', color: 'primary' },
    { label: 'login.features.knowledgeGrounded', color: 'accent' },
    { label: 'login.features.enterpriseSecurityIsolation', color: 'success' },
  ],

  // Visual treatments — intentionally absent so Brij tenants pick up the
  // defaultBrand values (semicircle OFF, chat untinted). The M² preset
  // enables both; Brij keeps the platform-neutral surface.
  // `agentAvatars` is absent for the same reason: no avatar template sets, so
  // Brij agents wear the default brand's mark — the Brij mark — in their colour
  // (the textures are M²'s).

  // `terminology` is also intentionally absent: pages fall back to i18n copy.
  // Tenants can set custom greetings in the White-Label Editor. (Symmetric with m2.ts.)
};

export default brij;
